import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import qz from 'qz-tray';
import { JSEncrypt } from 'jsencrypt';
import CryptoJS from 'crypto-js';
import axios from '../utils/axios';

const QZTrayContext = createContext(null);

// Static connection promise to prevent multiple connection attempts
let globalConnectionPromise = null;

// Base QZTray provider that requires settings
export const QZTrayProvider = ({ children, settings }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [printers, setPrinters] = useState([]);
    const [connectionPromise] = useState(null);
    const [connectionMutex] = useState(false);

    const updatePrinters = useCallback(async () => {
        try {
            const availablePrinters = await qz.printers.find();
    

            // Format printers and ensure they are strings
            const formattedPrinters = availablePrinters
                .filter(printer => printer && typeof printer === 'string' && printer.trim())
                .map(printer => ({
                    value: printer,
                    label: printer
                }));


            setPrinters(formattedPrinters);
        } catch (err) {
            console.error('Failed to get printers:', err);
            setPrinters([]); // Reset printers on error
        }
    }, []);

    const setupSecurity = useCallback((certificate, private_key) => {
        // Set up certificate
        qz.security.setCertificatePromise(function(resolve, reject) {
            resolve(certificate);
        });

        // Set up private key for signing
        qz.security.setSignaturePromise(function(toSign) {
            return function(resolve, reject) {
                try {
                    const sign = new JSEncrypt();
                    sign.setPrivateKey(private_key);
                    const signature = sign.sign(toSign, CryptoJS.SHA512, 'sha512');
                    resolve(signature);
                } catch (err) {
                    console.error('Error during signing:', err);
                    reject(err);
                }
            };
        });
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const connectQZ = useCallback(async () => {
        // If there's an existing global connection promise, wait for it
        if (globalConnectionPromise) {
            try {
                await globalConnectionPromise;
                const isActive = await qz.websocket.isActive();
                if (isActive) {
                    setIsConnected(true);
                    await updatePrinters();
                    return;
                }
            } catch (err) {
                // Previous connection failed, continue with new attempt
    
                globalConnectionPromise = null;
            }
        }

        // Validate settings
        const certificate = settings?.printing?.certificate;
        const private_key = settings?.printing?.private_key;
        
        if (!certificate || !private_key) {
            console.error('Certificate or private key not found in settings');
            return;
        }

        if (isConnecting) {

            return;
        }

        setIsConnecting(true);

        try {
            // Configure security before connecting
            setupSecurity(certificate, private_key);


            globalConnectionPromise = qz.websocket.connect();
            await globalConnectionPromise;

            qz.security.setSignatureAlgorithm('SHA512');

            

            setIsConnected(true);
            await updatePrinters();
        } catch (err) {
            console.error('Failed to connect to QZ Tray:', err);
            setIsConnected(false);
            setPrinters([]);
            globalConnectionPromise = null;
            throw err;
        } finally {
            setIsConnecting(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConnecting, updatePrinters, connectionPromise, settings, connectionMutex]);

    const disconnectQZ = useCallback(async () => {
        if (!await qz.websocket.isActive()) {
            setIsConnected(false);
            setPrinters([]);
            return;
        }
        
        try {
            await qz.websocket.disconnect();
            setIsConnected(false);
            setPrinters([]);
        } catch (err) {
            console.error('Failed to disconnect from QZ Tray:', err);
        }
    }, []);

    // Initial connection attempt
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        let mounted = true;

        const init = async () => {
            if (!mounted || !settings?.printing?.certificate || !settings?.printing?.private_key) {
                return;
            }

            try {
                await connectQZ();
            } catch (err) {
                if (mounted) {
                    console.error('Failed to initialize QZ Tray connection:', err);
                }
            }
        };

        // Only attempt connection if we have the required settings
        if (settings?.printing?.certificate && settings?.printing?.private_key) {
            init();
        }

        // Cleanup on unmount
        return () => {
            mounted = false;
            if (isConnected) {
                disconnectQZ();
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings?.printing?.certificate, settings?.printing?.private_key]); // Reconnect when certificate or key changes

    return (
        <QZTrayContext.Provider value={{
            isConnected,
            isConnecting,
            printers,
            connectQZ,
            disconnectQZ
        }}>
            {children}
        </QZTrayContext.Provider>
    );
};

export const useQZTray = () => {
    const context = useContext(QZTrayContext);
    if (!context) {
        throw new Error('useQZTray must be used within a QZTrayProvider');
    }
    return context;
};

// Wrapper component that fetches settings
export const QZTrayProviderWrapper = ({ children }) => {
    const [settings, setSettings] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let mounted = true;

        const loadSettings = async () => {
            try {
                const response = await axios.get('/settings');
                if (!mounted) return;

                if (response.data?.printing?.certificate && response.data?.printing?.private_key) {
                    setSettings(response.data);
                } else {
                    console.error('Settings loaded but missing certificate or private key');
                }
            } catch (error) {
                console.error('Error loading settings:', error);
            } finally {
                if (mounted) {
                    setIsLoading(false);
                }
            }
        };

        loadSettings();

        return () => {
            mounted = false;
        };
    }, []);

    if (isLoading) {
        return null; // or a loading spinner
    }

    return (
        <QZTrayProvider settings={settings}>
            {children}
        </QZTrayProvider>
    );
};
